import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import useSupabaseData from "../../hooks/useSupabaseData";
import useWhitelistManager from "../../hooks/useWhitelistManager";
import Header from "../Header";
import { REDIRECT_URL } from "../../utils/constants";
import {
  X,
  Plus,
  RefreshCw,
  Search,
  ExternalLink,
  Crown,
  Shield,
  UserMinus,
  UserPlus,
  Filter,
  AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import InviteUserModal from "./invite-user-modal";
import DeleteConfirmModal from "../ui/DeleteConfirmModal";
import { toast, Toaster } from "sonner";
import { ownerService } from "../../lib/ownerService";
import Alert from "../ui/Alert";

const PAGE_SIZE = 20;

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const AdminUserPanel = () => {
  const { user, isAdmin, isOwner, isRoleLoading } = useAuth();
  const {
    isLoading: loadingData,
    error: dataError,
    success: dataSuccess,
    clearMessages: clearDataMessages,
    pullAllAffiliateData,
  } = useSupabaseData();
  const { addToWhitelist } = useWhitelistManager();
  const [users, setUsers] = useState([]);
  const [affiliateData, setAffiliateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    is_admin: false,
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Owner functionality state
  const [adminData, setAdminData] = useState({
    owner: null,
    admins: [],
    totalCount: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [ownerError, setOwnerError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [showPerformanceData, setShowPerformanceData] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  // Remove the local admin status check since we're using AuthContext
  // const [isAdmin, setIsAdmin] = useState(false);
  // useEffect(() => {
  //   const fetchProfile = async () => {
  //     if (!user) return;
  //     const { data } = await supabase
  //       .from("profiles")
  //       .select("is_admin")
  //       .eq("id", user.id)
  //       .single();
  //     if (data?.is_admin) setIsAdmin(true);
  //     else setIsAdmin(false);
  //   };
  //   fetchProfile();
  // }, [user]);

  // Debounced search effect
  useEffect(() => {
    const handler = debounce((val) => setSearch(val), 400);
    handler(searchValue);
    return () => clearTimeout(handler);
  }, [searchValue]);

  // Load admin data if owner
  useEffect(() => {
    if (user?.id && isOwner) {
      loadAdminData();
    }
  }, [user?.id, isOwner]);

  // Auto-search for owner features
  useEffect(() => {
    if (!isOwner) return;

    const performSearch = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const results = await ownerService.searchUsers(user.id, searchTerm, 10);
        setSearchResults(results);
      } catch (err) {
        console.error("❌ Error searching users:", err);
        setOwnerError(err.message || "Failed to search users");
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, user?.id, isOwner]);

  const loadAdminData = async () => {
    try {
      console.log("🔄 Loading admin data for owner:", user.id);
      const data = await ownerService.getAllAdmins(user.id);
      console.log("✅ Admin data loaded:", data);
      setAdminData(data);
    } catch (err) {
      console.error("❌ Error loading admin data:", err);
      setOwnerError(err.message || "Failed to load admin data");
    }
  };

  const handleAddAdmin = async (targetUser) => {
    try {
      setOwnerError(null);
      setSuccessMessage("");

      const result = await ownerService.addAdmin(user.id, targetUser.id);

      if (result.success) {
        setSuccessMessage(
          `✅ ${result.adminUsername} has been promoted to admin!`
        );
        await loadAdminData();
        setSearchTerm("");
        setSearchResults([]);
        await fetchUsers(); // Refresh the main user list
      } else {
        setOwnerError(result.error || "Failed to add admin");
      }
    } catch (err) {
      console.error("❌ Error adding admin:", err);
      setOwnerError(err.message || "Failed to add admin");
    }
  };

  const handleRemoveAdmin = async (targetUser) => {
    try {
      setOwnerError(null);
      setSuccessMessage("");

      const result = await ownerService.removeAdmin(user.id, targetUser.id);

      if (result.success) {
        setSuccessMessage(
          `✅ ${result.formerAdminUsername} has been removed from admin role!`
        );
        await loadAdminData();
        await fetchUsers(); // Refresh the main user list
      } else {
        setOwnerError(result.error || "Failed to remove admin");
      }

      setConfirmAction(null);
    } catch (err) {
      console.error("❌ Error removing admin:", err);
      setOwnerError(err.message || "Failed to remove admin");
      setConfirmAction(null);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timeoutId = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [successMessage]);

  // Fetch users with search
  const fetchUsers = async (pageNum = 1, searchTerm = search) => {
    setLoading(true);
    const from = (pageNum - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    let query = supabase
      .from("profiles")
      .select("id,username,email,is_admin", { count: "exact" })
      .range(from, to);
    if (searchTerm) {
      // Use separate queries to avoid 406 errors
      const [usernameResults, emailResults] = await Promise.all([
        supabase
          .from("profiles")
          .select("id,username,email,is_admin", { count: "exact" })
          .range(from, to)
          .ilike("username", `%${searchTerm}%`),
        supabase
          .from("profiles")
          .select("id,username,email,is_admin", { count: "exact" })
          .range(from, to)
          .ilike("email", `%${searchTerm}%`),
      ]);

      // Combine results and deduplicate by id
      const combinedResults = new Map();
      usernameResults.data?.forEach((user) =>
        combinedResults.set(user.id, user)
      );
      emailResults.data?.forEach((user) => combinedResults.set(user.id, user));

      const { data, error } = {
        data: Array.from(combinedResults.values()),
        error: usernameResults.error || emailResults.error,
      };

      if (error) {
        toast.error(error.message);
      } else {
        setUsers(data);
        setTotal(data.length);
        // await fetchAffiliateData(); // Removed automatic call
      }
      setLoading(false);
      return;
    }
    const [{ data, error }, { count }] = await Promise.all([
      query,
      supabase
        .from("profiles")
        .select("*", { count: "exact", head: true }),
    ]);
    if (error) {
      toast.error(error.message);
    } else {
      setUsers(data);
      setTotal(count);

      // Fetch affiliate performance data for all users
      // await fetchAffiliateData(); // Removed automatic call
    }
    setLoading(false);
  };

  // Fetch affiliate performance data from finalized daily earnings
  const fetchAffiliateData = async () => {
    try {
      setPerformanceLoading(true);
      // Use RPC that reads from finalized_daily_ad_earnings for accurate commission
      const { data, error } = await supabase.rpc(
        "get_affiliate_performance_summary",
        {
          brand_filter: null,
          start_date: null,
          end_date: null,
          limit_count: 500,
        }
      );

      if (error) {
        console.error("Error fetching affiliate summary data:", error);
        return;
      }

      // Transform RPC response to match expected format
      const affiliateData = (data || []).map((row) => ({
        name: row.affiliate_name,
        totalAdSpend: parseFloat(row.total_ad_spend) || 0,
        totalRevenue: parseFloat(row.total_revenue) || 0,
        totalProfit: parseFloat(row.total_profit) || 0,
        totalCommission: parseFloat(row.total_commission) || 0,
        totalPurchases: parseInt(row.total_purchases) || 0,
        adCount: parseInt(row.ad_count) || 0,
        avgROAS: parseFloat(row.avg_roas) || 0,
      }));

      setAffiliateData(affiliateData);
      setShowPerformanceData(true);
    } catch (err) {
      console.error("Error fetching affiliate data:", err);
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Get affiliate performance for a user
  const getAffiliatePerformance = (username) => {
    if (!username || !affiliateData.length) return null;

    // Find affiliate data by username
    const affiliate = affiliateData.find(
      (aff) => aff.name.toLowerCase() === username.toLowerCase()
    );

    return affiliate || null;
  };

  useEffect(() => {
    if (isAdmin) fetchUsers(page, search);
    // eslint-disable-next-line
  }, [isAdmin, page, search]);

  // Add new user (invite)
  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      // 1. Check username uniqueness
      const { data: existingUsername, error: usernameError } =
        await supabase
          .from("profiles")
          .select("id")
          .eq("username", newUser.username)
          .single();

      if (usernameError && usernameError.code !== "PGRST116") {
        throw new Error("Error checking for existing username");
      }

      if (existingUsername) {
        throw new Error(
          `Username "${newUser.username}" is already taken. Please choose a different username.`
        );
      }

      // 2. Check email uniqueness
      const { data: existingEmail, error: emailError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", newUser.email)
        .single();

      if (emailError && emailError.code !== "PGRST116") {
        throw new Error("Error checking for existing email");
      }

      if (existingEmail) {
        throw new Error(
          `Email "${newUser.email}" is already registered. Please use a different email.`
        );
      }

      // 3. Create the user + profile via admin edge function (service_role server-side)
      const { data: createData, error: invokeError } =
        await supabase.functions.invoke("admin-user-management", {
          body: {
            action: "createUserWithProfile",
            email: newUser.email,
            username: newUser.username,
            is_admin: newUser.is_admin,
            autoVerify: true,
            sendInvite: true,
            redirectTo: REDIRECT_URL + "/reset-password",
          },
        });

      if (invokeError) {
        throw new Error(invokeError.message || "Failed to invite user");
      }

      if (createData?.error) {
        const { kind, message } = createData.error;
        // Map domain errors to the same user-facing messages as before
        if (kind === "username_taken") {
          throw new Error(
            `Username "${newUser.username}" is already taken. Please choose a different username.`
          );
        }
        if (kind === "email_taken" || kind === "email_already_registered") {
          throw new Error(
            `Email "${newUser.email}" is already registered. Please use a different email.`
          );
        }
        if (kind === "reset_failed") {
          throw new Error(
            message || "Failed to send password reset email for admin user"
          );
        }
        throw new Error(message || "Failed to invite user");
      }

      // 4. Add user to affiliate whitelist
      console.log("📋 Adding invited user to affiliate whitelist...");
      try {
        await addToWhitelist(newUser.username, [], false); // Add with no patterns, no auto-reassign
        console.log(
          "✅ Invited user added to affiliate whitelist successfully!"
        );
      } catch (whitelistError) {
        console.warn(
          "⚠️ Failed to add invited user to whitelist:",
          whitelistError.message
        );
        // Don't fail the entire process if whitelist addition fails
      }
      toast.success(
        "User invited successfully! Email auto-verified (admin created). Password setup email sent. Added to affiliate whitelist."
      );
      setNewUser({ username: "", email: "", is_admin: false });
      fetchUsers(page);
      setShowInviteModal(false);
    } catch (err) {
      toast.error(err.message || "Failed to invite user");
    }
    setInviteLoading(false);
  };

  // Update admin status
  const handleAdminToggle = async (id, is_admin) => {
    setLoading(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "admin-user-management",
        {
          body: { action: "setAdminStatus", userId: id, isAdmin: !is_admin },
        }
      );

      if (invokeError) throw new Error(invokeError.message);
      if (data?.error) {
        toast.error(data.error.message || "Failed to update admin status");
        return;
      }

      toast.success(
        `Admin status ${!is_admin ? "granted" : "revoked"} successfully.`
      );
      fetchUsers(page);
    } catch (err) {
      toast.error(err.message || "Failed to update admin status");
    }
    setLoading(false);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Confirm delete user
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "admin-user-management",
        {
          body: { action: "deleteUser", userId: userToDelete.id },
        }
      );

      if (invokeError) throw new Error(invokeError.message);
      if (data?.error) {
        const { stage, message } = data.error;
        toast.error(
          stage
            ? `Failed to delete user (${stage}): ${message}`
            : message || "Failed to delete user"
        );
        return;
      }

      toast.success(`User "${userToDelete.username}" deleted successfully.`);
      fetchUsers(page);
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    }
    setIsDeleting(false);
  };

  // Show loading state while checking admin status
  if (isRoleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-gray-300">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show permission error only after loading is complete
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-red-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-red-400 mb-4">
            You do not have permission to view this page.
          </p>
          <p className="text-gray-400">
            This area is restricted to administrators only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
      {/* Noise texture overlay */}
      <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none"></div>

      {/* Sonner Toaster */}
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "white",
          },
        }}
      />

      <Header />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Owner Controls */}
        {isOwner && (
          <div className="mb-6 space-y-4">
            {/* Messages */}
            {ownerError && (
              <Alert type="error" className="mb-4">
                {ownerError}
                <button
                  onClick={() => setOwnerError(null)}
                  className="ml-4 px-3 py-1 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded"
                >
                  Dismiss
                </button>
              </Alert>
            )}

            {successMessage && (
              <Alert type="success" className="mb-4">
                {successMessage}
              </Alert>
            )}

            {/* Owner Controls Panel */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-yellow-500" />
                  <h3 className="text-xl font-bold text-white">
                    Owner Controls
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    Admin Management
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Admins */}
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    Current Admins (
                    {adminData.adminCount + (adminData.owner ? 1 : 0)})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {/* Owner */}
                    {adminData.owner && (
                      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-yellow-500" />
                            <span className="text-white font-medium">
                              {adminData.owner.username}
                            </span>
                          </div>
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                            Owner
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Admins */}
                    {adminData.admins.map((admin) => (
                      <div
                        key={admin.id}
                        className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-400" />
                            <span className="text-white font-medium">
                              {admin.username}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                              Admin
                            </span>
                            <button
                              onClick={() =>
                                setConfirmAction({
                                  type: "remove",
                                  user: admin,
                                })
                              }
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-1 rounded"
                              title="Remove admin privileges"
                            >
                              <UserMinus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {adminData.admins.length === 0 && (
                      <div className="text-center py-4 text-gray-400 text-sm">
                        <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No admins yet</p>
                        <p className="text-xs">
                          Add your first admin using the search →
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add New Admin */}
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-green-400" />
                    Add New Admin
                  </h4>

                  {/* Search */}
                  <div className="mb-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm placeholder-gray-400 focus:border-green-500/50 focus:outline-none"
                      />
                      {isSearching && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <RefreshCw className="w-4 h-4 text-green-400 animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {searchResults.map((searchUser) => (
                      <div
                        key={searchUser.id}
                        className="bg-black/30 border border-white/10 rounded-lg p-3 text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-white font-medium">
                              {searchUser.username}
                            </div>
                            <div className="text-xs text-gray-400">
                              {searchUser.user_type}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddAdmin(searchUser)}
                            disabled={searchUser.is_admin}
                            className={`${
                              searchUser.is_admin
                                ? "bg-gray-500/20 text-gray-500 cursor-not-allowed text-xs px-2 py-1"
                                : "bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs px-2 py-1"
                            } rounded`}
                          >
                            {searchUser.is_admin
                              ? "Already Admin"
                              : "Make Admin"}
                          </button>
                        </div>
                      </div>
                    ))}

                    {searchTerm &&
                      !isSearching &&
                      searchResults.length === 0 && (
                        <div className="text-center py-4 text-gray-400 text-sm">
                          No users found for "{searchTerm}"
                        </div>
                      )}

                    {!searchTerm && (
                      <div className="text-center py-4 text-gray-400 text-sm">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Search for users to promote to admin</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <header className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold mb-1 text-white">
                Team Management & Performance
              </h2>
              <div className="text-gray-300 text-sm">
                Holistic Hercules &bull; {total} members &bull;{" "}
                {showPerformanceData
                  ? "Real-time affiliate performance data"
                  : 'Click "Load Performance Data" to view earnings'}
              </div>
            </div>
            <div className="flex gap-2">
              {!showPerformanceData && (
                <button
                  className="flex items-center gap-2 bg-blue-600/80 backdrop-blur-xl border border-blue-500/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  onClick={fetchAffiliateData}
                  disabled={performanceLoading}
                >
                  {performanceLoading ? (
                    <RefreshCw className="animate-spin h-5 w-5" />
                  ) : (
                    <RefreshCw className="h-5 w-5" />
                  )}
                  Load Performance Data
                </button>
              )}
              <button
                className="flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-[#B8460E]/20 transition-colors text-sm"
                onClick={() => setShowInviteModal(true)}
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" /> Invite Member
              </button>
              <button
                className="flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-white/20 transition-colors text-sm"
                onClick={() => fetchUsers(page, search)}
                disabled={loading}
              >
                <RefreshCw
                  className={
                    loading
                      ? "animate-spin h-4 w-4 sm:h-5 sm:w-5"
                      : "h-4 w-4 sm:h-5 sm:w-5"
                  }
                />{" "}
                Refresh
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 text-gray-400 h-5 w-5" />
              <input
                type="text"
                className="w-full bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder-gray-400 pl-10 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                placeholder="Search team members..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>
        </header>

        <div className="overflow-x-auto rounded-lg shadow bg-white/5 backdrop-blur-xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-white/10 backdrop-blur-xl">
                <th className="py-3 px-4 border-b border-white/10 text-left text-white font-medium">
                  Username
                </th>
                <th className="py-3 px-4 border-b border-white/10 text-left text-white font-medium">
                  Email
                </th>
                {showPerformanceData && (
                  <>
                    <th className="py-3 px-4 border-b border-white/10 text-right text-white font-medium">
                      Total Earnings
                    </th>
                    <th className="py-3 px-4 border-b border-white/10 text-center text-white font-medium">
                      Ad Count
                    </th>
                    <th className="py-3 px-4 border-b border-white/10 text-right text-white font-medium">
                      Total Spend
                    </th>
                    <th className="py-3 px-4 border-b border-white/10 text-center text-white font-medium">
                      Avg ROAS
                    </th>
                  </>
                )}
                <th className="py-3 px-4 border-b border-white/10 text-center text-white font-medium">
                  Admin
                </th>
                <th className="py-3 px-4 border-b border-white/10 text-center text-white font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={showPerformanceData ? 8 : 4}
                    className="text-center py-6 text-white"
                  >
                    <div className="flex justify-center items-center gap-2">
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={showPerformanceData ? 8 : 4}
                    className="text-center py-6"
                  >
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <X className="h-8 w-8" />
                      <span>No team members found</span>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const affiliatePerformance = getAffiliatePerformance(
                    u.username
                  );
                  return (
                    <tr
                      key={u.id}
                      className="hover:bg-white/10 transition-colors border-b border-white/5"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">
                            {u.username}
                          </span>
                          {affiliatePerformance && (
                            <Link
                              to={`/affiliate/${encodeURIComponent(u.username)}`}
                              className="text-[#B8460E] hover:text-[#8B3A0E] transition-colors"
                              title="View affiliate details"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300">{u.email}</td>
                      {showPerformanceData && (
                        <>
                          <td className="py-3 px-4 text-right font-medium">
                            {affiliatePerformance ? (
                              <span
                                className={
                                  affiliatePerformance.totalCommission > 0
                                    ? "text-green-400"
                                    : "text-gray-400"
                                }
                              >
                                $
                                {affiliatePerformance.totalCommission.toFixed(
                                  2
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-400">$0.00</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-white">
                            {affiliatePerformance
                              ? affiliatePerformance.adCount
                              : 0}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {affiliatePerformance ? (
                              <span className="text-gray-300">
                                ${affiliatePerformance.totalAdSpend.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-400">$0.00</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {affiliatePerformance &&
                            affiliatePerformance.avgROAS > 0 ? (
                              <span
                                className={
                                  affiliatePerformance.avgROAS >= 2
                                    ? "text-green-400"
                                    : "text-orange-400"
                                }
                              >
                                {affiliatePerformance.avgROAS.toFixed(2)}x
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </>
                      )}
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={u.is_admin}
                          onChange={() => handleAdminToggle(u.id, u.is_admin)}
                          disabled={loading || u.is_admin}
                          className="w-4 h-4 text-[#B8460E] bg-white/10 border-white/20 rounded focus:ring-[#B8460E] focus:ring-2"
                        />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          className="text-red-400 hover:text-red-300 hover:underline disabled:opacity-50 transition-colors"
                          onClick={() => handleDeleteClick(u)}
                          disabled={loading || user?.id === u.id}
                          title={
                            user?.id === u.id
                              ? "You cannot delete yourself"
                              : "Delete user"
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4">
          <button
            className="px-3 py-1 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <span className="text-gray-300">
            Page {page} of {Math.ceil(total / PAGE_SIZE) || 1}
          </span>
          <button
            className="px-3 py-1 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * PAGE_SIZE >= total || loading}
          >
            Next
          </button>
        </div>
      </div>
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        loading={inviteLoading}
        newUser={newUser}
        setNewUser={setNewUser}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message="Are you sure you want to delete this user? This will permanently remove their account and all associated data."
        confirmText="Delete User"
        cancelText="Cancel"
        isLoading={isDeleting}
        userName={userToDelete?.username}
      />

      {/* Owner Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900/95 border border-red-500/30 rounded-xl p-6 max-w-md w-full">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Confirm Action
              </h3>
              <p className="text-gray-400 mb-6">
                Are you sure you want to remove admin privileges from{" "}
                <span className="text-white font-medium">
                  {confirmAction.user.username}
                </span>
                ?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveAdmin(confirmAction.user)}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 py-2 px-4 rounded-lg transition-colors"
                >
                  Remove Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserPanel;
